import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { magicFlowApi } from 'src/services';
import type {
  Flow,
  FlowRunPriority,
  FlowRun,
  FlowRunStatus,
  MagicFlowOption,
} from 'src/models';

type UseMagicFlowFlowRunsControllerParams = {
  orgUuid?: string;
  hasOrgSelected: boolean;
  scopedHeaders: Record<string, string | boolean>;
};

type FlowRunListResponse = {
  flow_runs?: FlowRun[];
  page?: number;
  page_size?: number;
  total?: number;
};

type FlowRunStatusListResponse = {
  statuses?: FlowRunStatus[];
};

type FlowRunPriorityListResponse = {
  priorities?: FlowRunPriority[];
};

type FlowListResponse = {
  flows?: Flow[];
};

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

export const useMagicFlowFlowRunsController = ({
  orgUuid,
  hasOrgSelected,
  scopedHeaders,
}: UseMagicFlowFlowRunsControllerParams) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [flowRunStatusIds, setFlowRunStatusIds] = useState<string[]>([]);
  const [flowRunPriorityIds, setFlowRunPriorityIds] = useState<string[]>([]);
  const [flowRunFlowIds, setFlowRunFlowIds] = useState<string[]>([]);
  const [flowRunSearch, setFlowRunSearch] = useState<string>('');

  const { data: flowRunStatuses = [] } = useQuery({
    queryKey: ['magicflow-flow-run-statuses', orgUuid],
    enabled: hasOrgSelected,
    queryFn: () =>
      magicFlowApi
        .get<FlowRunStatusListResponse>('/flow_run_status', {
          headers: scopedHeaders,
        })
        .then((res) => res.data?.statuses || []),
  });

  const { data: flowRunPriorities = [] } = useQuery({
    queryKey: ['magicflow-flow-run-priorities', orgUuid],
    enabled: hasOrgSelected,
    queryFn: () =>
      magicFlowApi
        .get<FlowRunPriorityListResponse>('/flow_run_priority', {
          headers: scopedHeaders,
        })
        .then((res) => res.data?.priorities || []),
  });

  const { data: flowsForFilter = [] } = useQuery({
    queryKey: ['magicflow-flows-filter', orgUuid],
    enabled: hasOrgSelected,
    queryFn: () =>
      magicFlowApi
        .get<FlowListResponse>('/flow', {
          headers: scopedHeaders,
          params: {
            page: 1,
            page_size: 500,
            latest_only: false,
            active_only: false,
          },
        })
        .then((res) => res.data?.flows || []),
  });

  const {
    data: flowRunListData,
    isLoading: isLoadingFlowRuns,
    isFetching: isFetchingFlowRuns,
    refetch: refetchFlowRuns,
  } = useQuery({
    queryKey: [
      'magicflow-flow-runs-list',
      orgUuid,
      page,
      pageSize,
      flowRunStatusIds,
      flowRunPriorityIds,
      flowRunFlowIds,
      flowRunSearch,
    ],
    enabled: hasOrgSelected,
    queryFn: () =>
      magicFlowApi
        .get<FlowRunListResponse>('/flow_run', {
          headers: scopedHeaders,
          params: {
            page,
            page_size: pageSize,
            sort_by: 'updated_at',
            sort_direction: 'desc',
            ...(flowRunStatusIds.length
              ? { status_ids: flowRunStatusIds.join(',') }
              : {}),
            ...(flowRunPriorityIds.length
              ? { priority_ids: flowRunPriorityIds.join(',') }
              : {}),
            ...(flowRunFlowIds.length
              ? { flow_ids: flowRunFlowIds.join(',') }
              : {}),
            ...(flowRunSearch ? { search: flowRunSearch } : {}),
          },
        })
        .then((res) => res.data),
  });

  const flowRuns = flowRunListData?.flow_runs || [];
  const flowRunsTotal = flowRunListData?.total ?? 0;

  const flowRunStatusOptions = useMemo<MagicFlowOption[]>(
    () =>
      (flowRunStatuses || [])
        .filter((s) => s.flow_run_status_id)
        .map((s) => ({
          value: String(s.flow_run_status_id),
          label: s.name || s.key,
        })),
    [flowRunStatuses]
  );

  const flowRunPriorityOptions = useMemo<MagicFlowOption[]>(
    () =>
      (flowRunPriorities || [])
        .filter((p) => p.flow_run_priority_id)
        .map((p) => ({
          value: String(p.flow_run_priority_id),
          label: p.name || p.key,
        })),
    [flowRunPriorities]
  );

  const flowRunFlowOptions = useMemo<MagicFlowOption[]>(() => {
    const latestByKey = new Map<string, Flow>();
    (flowsForFilter || []).forEach((f) => {
      if (!f.flow_id) return;
      const groupKey = `${f.key ?? f.flow_id}::${f.namespace_id ?? ''}`;
      const existing = latestByKey.get(groupKey);
      if (!existing || (f.version ?? 0) > (existing.version ?? 0)) {
        latestByKey.set(groupKey, f);
      }
    });
    return Array.from(latestByKey.values()).map((f) => ({
      value: f.flow_id,
      label: f.name,
    }));
  }, [flowsForFilter]);

  const flowsById = useMemo<Record<string, Flow>>(() => {
    const map: Record<string, Flow> = {};
    (flowsForFilter || []).forEach((f) => {
      if (f.flow_id) {
        map[f.flow_id] = f;
      }
    });
    return map;
  }, [flowsForFilter]);

  const flowRunsTableData = useMemo(
    () =>
      (flowRuns || []).map((run, index) => ({
        ...run,
        key: String(
          run.flow_run_id || run.updated_at || run.created_at || index
        ),
      })),
    [flowRuns]
  );

  return {
    flowRunsTableData,
    flowRunsTotal,
    isLoadingFlowRuns,
    isFetchingFlowRuns,
    refreshFlowRuns: () => refetchFlowRuns(),
    flowRunPage: page,
    setFlowRunPage: setPage,
    flowRunPageSize: pageSize,
    setFlowRunPageSize: setPageSize,
    flowRunStatusIds,
    setFlowRunStatusIds,
    flowRunPriorityIds,
    setFlowRunPriorityIds,
    flowRunFlowIds,
    setFlowRunFlowIds,
    flowRunSearch,
    setFlowRunSearch,
    flowRunStatusOptions,
    flowRunPriorityOptions,
    flowRunFlowOptions,
    flowRunFlowsById: flowsById,
  };
};
